import { useState } from "react";

import { useDetalheEscopoMacroDados } from "../../../../hooks/use-planejamento-dados";
import { AGUARDANDO_APROVACAO } from "../planejamento-controle.constants";
import { textoSemCotacao } from "../projecao-formulario/projecao-formulario.custo-padrao";
import { formatCompetencia, formatMoeda, formatPercentual } from "../planejamento-controle.format";
import {
  DATAS_DA_OBRA_PREFIXO,
  EDITAR_MOTIVO,
  MATERIAIS_TITLE,
  NOTA_TABELA,
  NO_VALUE,
  SEM_PROJECAO_AVISO,
  SEM_PROJECAO_COLETORES,
  SERVICOS_TITLE,
  SUFIXO_FIM,
  SUFIXO_INICIO,
} from "./detalhe-escopo-macro.constants";
import {
  calcularVariacao,
  categoriaSemProjecao,
  competenciasDe,
  emReais,
  fatorNoTotal,
  montarCategoria,
  montarLinhasColetor,
  rotuloCentroCusto,
  rotuloClassificacao,
} from "./detalhe-escopo-macro.montagem";
import type { DetalheEscopoMacroProps } from "./types-detalhe-escopo-macro";

// Figma `tabela-coletores`: Coletor 352 (fixo), Total 130 e um mês por competência (120; 140 no mês de início).
const COLUNAS_FIXAS = "352px 130px";
const COLUNA_MES = "120px";
const COLUNA_MES_INICIO = "140px";

/**
 * Detalhe somente leitura do Escopo Macro (HU 2709): lê a consulta do Model e entrega tudo formatado (reais com 2 casas,
 * mensagens de ausência de custo padrão e cotação, variação vs custo padrão calculada aqui). Abrir não altera nada; Fechar e X voltam à aba.
 */
export function useDetalheEscopoMacro({
  obraId,
  obraEscopoMacroId,
  onClose,
  editar,
}: DetalheEscopoMacroProps) {
  const consulta = useDetalheEscopoMacroDados(obraId, obraEscopoMacroId);
  const detalhe = consulta.data;
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(new Set());

  const projecao = detalhe?.projecao ?? null;
  const cotacao = detalhe?.cotacao ?? null;
  const custoPadrao = projecao?.custoPadrao ?? null;
  const fator = projecao?.fatorIdade ?? null;
  const totais = detalhe?.totais ?? null;
  const inicioObra = detalhe?.obra.dataInicio?.slice(0, 7) ?? null;
  const fimObra = detalhe?.obra.dataFim?.slice(0, 7) ?? null;
  const competencias = projecao
    ? competenciasDe(projecao).map((rotulo, indice) => {
        const mes = projecao.mensalizacao[indice]?.competencia.slice(0, 7);
        if (mes === inicioObra) return `${rotulo}${SUFIXO_INICIO}`;
        return mes === fimObra ? `${rotulo}${SUFIXO_FIM}` : rotulo;
      })
    : [];
  // Sem fator o core manda 0 (Tipo 100) ou `null`: nos dois casos ele não se aplica (HU CA28).
  const percentualFator = fator ? `+${formatPercentual(fator)}` : null;

  let estado: "carregando" | "proibido" | "erro" | "sem-projecao" | "pronto" = "pronto";
  if (consulta.status === "forbidden") estado = "proibido";
  else if (consulta.status === "error") estado = "erro";
  else if (consulta.status === "loading" || !detalhe) estado = "carregando";
  else if (!projecao) estado = "sem-projecao";

  // Total antes do fator: soma das categorias em reais, só quando todas convertem (moeda sem cotação fica de fora, RN06).
  const projetados = projecao
    ? [projecao.servicos, projecao.materiais]
        .filter((parametro) => parametro !== null)
        .map((parametro) => emReais(parametro.valor, parametro.moeda, cotacao))
    : [];
  const projetadoReais = projetados.every((valor) => valor !== null)
    ? projetados.reduce<number>((soma, valor) => soma + (valor ?? 0), 0)
    : null;

  return {
    estado,
    editar: {
      desabilitado: !editar?.liberado,
      motivo: editar?.motivo ?? EDITAR_MOTIVO,
      abrir: editar?.abrir ?? (() => undefined),
    },
    errorMessage: consulta.errorMessage,
    recarregar: consulta.reload,
    fechar: onClose,
    cabecalho: detalhe
      ? {
          codigo: detalhe.tipoObra.codigo,
          nome: detalhe.nome,
          subtitulo: [
            `${detalhe.tipoObra.codigo} · ${detalhe.tipoObra.nome}`,
            detalhe.obra.embarcacao?.nome,
            ...Object.values(detalhe.camposTipo ?? {}),
            rotuloCentroCusto(detalhe.centroCusto),
          ]
            .filter((parte) => Boolean(parte))
            .join(" · "),
          classificacao: projecao ? rotuloClassificacao(projecao.classificacao) : null,
          aguardandoAprovacao:
            detalhe.solicitacaoPendente === "REVISAO_PROJECAO" ? AGUARDANDO_APROVACAO : null,
        }
      : null,
    campos: detalhe
      ? {
          nome: detalhe.nome,
          origem: detalhe.origem?.nome ?? NO_VALUE,
          referenciaMensalizacao: `${DATAS_DA_OBRA_PREFIXO} ${formatCompetencia(detalhe.obra.dataInicio)} a ${formatCompetencia(detalhe.obra.dataFim)}`,
        }
      : null,
    cotacao: cotacao ? `${cotacao.moeda} = ${formatMoeda(cotacao.valor, "BRL")}` : null,
    custoPadrao: custoPadrao
      ? [
          ...(custoPadrao.servicos === null
            ? []
            : [`Serviços: ${formatMoeda(custoPadrao.servicos, "BRL")}`]),
          ...(custoPadrao.materiais === null
            ? []
            : [`Materiais: ${formatMoeda(custoPadrao.materiais, "BRL")}`]),
          ...custoPadrao.semCotacao.map(textoSemCotacao),
          `Total: ${formatMoeda(custoPadrao.total, "BRL")}`,
        ].join(" · ")
      : null,
    fatorIdade: percentualFator === null ? null : `${percentualFator} sobre o valor projetado`,
    // Rótulo do valor com fator nos cards (Figma "Com fator de idade (+15%): …").
    rotuloComFator:
      percentualFator === null ? "Com fator de idade" : `Com fator de idade (${percentualFator})`,
    servicos: projecao
      ? montarCategoria(
          SERVICOS_TITLE,
          projecao.servicos,
          custoPadrao?.servicos,
          projecao,
          cotacao,
          totais && percentualFator ? totais.servicos : null,
        )
      : categoriaSemProjecao(SERVICOS_TITLE),
    materiais: projecao
      ? montarCategoria(
          MATERIAIS_TITLE,
          projecao.materiais,
          custoPadrao?.materiais,
          projecao,
          cotacao,
          totais && percentualFator ? totais.materiais : null,
        )
      : categoriaSemProjecao(MATERIAIS_TITLE),
    total: totais
      ? {
          valor: formatMoeda(totais.total, "BRL"),
          projetado: projetadoReais === null ? null : formatMoeda(projetadoReais, "BRL"),
          // Parcela do fator de idade (Figma `composicao-total`): o total já corrigido menos o projetado.
          fator: fatorNoTotal(totais.total, projetadoReais, percentualFator),
          variacao: calcularVariacao(
            projetadoReais,
            custoPadrao?.total,
            projecao?.usaCustoPadrao ?? true,
          ),
        }
      : { valor: NO_VALUE, projetado: null, fator: null, variacao: null },
    avisoSemProjecao: SEM_PROJECAO_AVISO,
    notaColetores: projecao
      ? NOTA_TABELA(
          detalhe?.tipoObra.codigo ?? "",
          formatCompetencia(detalhe?.obra.dataInicio),
          formatCompetencia(detalhe?.obra.dataFim),
        )
      : SEM_PROJECAO_COLETORES,
    competencias,
    colunasTabela: [
      COLUNAS_FIXAS,
      ...competencias.map((rotulo) =>
        rotulo.endsWith(SUFIXO_INICIO) ? COLUNA_MES_INICIO : COLUNA_MES,
      ),
    ].join(" "),
    linhas: detalhe && projecao ? montarLinhasColetor(detalhe, projecao, abertas) : [],
    alternarCategoria: (key: string) =>
      setAbertas((atual) => {
        const proximo = new Set(atual);
        if (!proximo.delete(key)) proximo.add(key);
        return proximo;
      }),
  };
}
